import type { TFootnoteEntryElement } from '@alpod-ai-writer/plate-plugins/footnotes';
import { FootnoteEntryPlugin, FootnotesPlugin, useFootnoteList, useFootnoteOpenState, useFootnotesOffset } from '@alpod-ai-writer/plate-plugins/footnotes/react';
import { BaseSuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion';
import { cn } from '@udecode/cn';
import { useEditorPlugin } from '@udecode/plate/react';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { useInlineEditor } from '../../hooks/useInlineEditor.hook';
import { FootnoteEditor } from './footnoteEditor.component';
import { InlineEditorReadonly } from './inlineEditorReadonly.component';

function jsonContainsSuggestionKey(nodes: unknown[], keyId: string): boolean {
    for (const node of nodes) {
        if (!node || typeof node !== 'object') continue;
        const n = node as Record<string, unknown>;
        if (n[keyId]) return true;
        if (Array.isArray(n.children) && jsonContainsSuggestionKey(n.children as unknown[], keyId)) return true;
    }
    return false;
}

function applyToJsonNodes(
    nodes: unknown[],
    keyId: string,
    suggestionId: string,
    action: 'accept' | 'reject',
): unknown[] {
    const filterOutType = action === 'accept' ? 'remove' : 'insert';

    const filtered = nodes.filter((node) => {
        if (!node || typeof node !== 'object') return true;
        const n = node as Record<string, unknown>;
        const data = n[keyId] as { type?: string; id?: string } | undefined;
        return !data || data.type !== filterOutType || data.id !== suggestionId;
    });

    return filtered.map((node) => {
        if (!node || typeof node !== 'object') return node;
        const n = node as Record<string, unknown>;

        if (!n[keyId]) {
            if (Array.isArray(n.children)) {
                return { ...n, children: applyToJsonNodes(n.children as unknown[], keyId, suggestionId, action) };
            }
            return node;
        }

        const data = n[keyId] as { type?: string; id?: string } | undefined;
        if (data?.id !== suggestionId) {
            if (Array.isArray(n.children)) {
                return { ...n, children: applyToJsonNodes(n.children as unknown[], keyId, suggestionId, action) };
            }
            return node;
        }

        // This node has the target suggestion key — remove it
        const cleaned: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(n)) {
            if (k === keyId) continue;
            cleaned[k] = v;
        }

        const hasOtherSuggestions = Object.keys(cleaned).some(k => k.startsWith(`${BaseSuggestionPlugin.key}_`));
        if (!hasOtherSuggestions) {
            delete cleaned[BaseSuggestionPlugin.key];
        }

        if (Array.isArray(cleaned.children)) {
            cleaned.children = applyToJsonNodes(cleaned.children as unknown[], keyId, suggestionId, action);
        }

        return cleaned;
    });
}

export const BlockFootnote = memo(() => {
    const docEditor = useDocumentEditor();
    const footnotesPlugin = useEditorPlugin(FootnotesPlugin);
    const footnotesOffset = useFootnotesOffset();
    const footnotes = useFootnoteList({ debounceMs: 300 });
    const { open } = useFootnoteOpenState();

    const footnotesEditorValue = useMemo(() => {
        return footnotes
            .map((item) => {
                if (!item) {
                    return ;
                }

                const node: TFootnoteEntryElement = {
                    type: 'footnote-entry',
                    id: item[0].id,
                    index: item[0].index,
                    children: item[0].footnotes ?? [{ text: '' }],
                };

                return node;
            })
            .filter((item) => item !== undefined) as TFootnoteEntryElement[];
    }, [ footnotes ]);

    const inlineEditor = useInlineEditor({
        document: docEditor.document,
        cursorData: docEditor.cursorData,
        initialValue: footnotesEditorValue,
    });
    
    useEffect(() => {
        if (inlineEditor) {
            inlineEditor.setOption(FootnoteEntryPlugin, '_preventDelete', false);
            inlineEditor.tf.setValue(footnotesEditorValue);
            inlineEditor.setOption(FootnoteEntryPlugin, '_preventDelete', true);
        }
    }, [ inlineEditor, footnotesEditorValue ]);

    useEffect(() => {
        if (inlineEditor) {
            inlineEditor.setOption(FootnoteEntryPlugin, 'footnoteIndexOffset', footnotesOffset);
        }
    }, [ inlineEditor, footnotesOffset ]);

    useEffect(() => {
        if (inlineEditor) {
            inlineEditor.setOption(FootnoteEntryPlugin, 'onSelect', open);
        }
    }, [ inlineEditor, open ]);

    const applyFootnoteSuggestion = useCallback((_action: 'accept' | 'reject', _inlineEd: unknown, keyId: string, suggestionId: string) => {
        const action = _action;
        // Operate purely on the main editor's JSON — no inline editor modification needed.
        // 'accept' removes 'remove'-type nodes and cleans marks from 'insert'/'update' nodes.
        // 'reject' removes 'insert'-type nodes and cleans marks from 'remove' nodes.
        const allFootnotes = footnotesPlugin.api.footnotes.getAll();

        for (const [fnNode, fnPath] of allFootnotes) {
            const footnotes = (fnNode as any).footnotes as unknown[] | undefined;
            if (!Array.isArray(footnotes)) continue;
            if (!jsonContainsSuggestionKey(footnotes, keyId)) continue;

            const newContent = applyToJsonNodes(footnotes, keyId, suggestionId, action);
            footnotesPlugin.tf.footnotes.setContents(fnPath, newContent as any);
            return;
        }
    }, [ footnotesPlugin ]);

    useEffect(() => {
        if (!inlineEditor) return;

        inlineEditor.setOption(FootnoteEntryPlugin, 'onSuggestionAccepted', (inlineEd: any, keyId: string, suggestionId: string) => {
            applyFootnoteSuggestion('accept', inlineEd, keyId, suggestionId);
        });
        inlineEditor.setOption(FootnoteEntryPlugin, 'onSuggestionRejected', (inlineEd: any, keyId: string, suggestionId: string) => {
            applyFootnoteSuggestion('reject', inlineEd, keyId, suggestionId);
        });
    }, [ inlineEditor, applyFootnoteSuggestion ]);

    return (
        <>
            <div
                className={cn(
                    'relative pt-4',
                    'before:block before:absolute before:top-0 before:left-0 before:w-1/3 before:h-[1px] before:bg-gray-400'
                )}
            >
                {footnotes.length > 0 && (
                    <InlineEditorReadonly
                        editor={inlineEditor}
                    />
                )}
                {footnotes.length === 0 && (
                    <p className='text-sm text-gray-500 italic'>
                        Do této sekce zatím nebyly vloženy žádné poznámky pod čarou.
                    </p>
                )}
            </div>
            {<FootnoteEditor />}
        </>
    );
}, () => true);
